import {
  BonusPeriodModel,
  PreviousBonusPeriodEmployeesModel,
} from 'api-hooks/bonus';
import {
  CalculationTypeEnum,
  EmployeeGradeEnum,
  EmployeeJobLevelEnum,
  SaleDepartmentEnum,
} from 'common/constants/enum';
import { fixFloatingPoint } from 'common/utils/number';
import React from 'react';

import {
  BonusPeriodFormType,
  EmployeeFormType,
  EmployeeJobLevelConfigurationFormType,
  EmployeePerformanceConfigurationFormType,
  SaleConfigurationFormType,
} from '../components/form-type';

export function onAddNewEmployee(): EmployeeFormType {
  return {
    customId: crypto.randomUUID(),
    employeeId: '',
    grade: '',
    id: undefined,
    jobLevel: '',
    previousGrade: undefined,
    role: undefined,
    previousJobLevel: undefined,
  };
}

export function generateDividenPercentage(percentage?: number, count = 0) {
  if (typeof percentage !== 'number') return percentage;
  if (typeof count !== 'number') return percentage;
  if (count === 0) return percentage;
  const dividenPercentage = fixFloatingPoint(percentage / count);
  return dividenPercentage;
}

export function generatePercentage(percentage: any, count = 0) {
  if (typeof percentage !== 'number') return percentage;
  if (typeof count !== 'number') return percentage;
  if (count === 0) return percentage;
  const _percentage = fixFloatingPoint(percentage * count);
  return _percentage;
}

export function generatePerformanceByEmployees(
  performances: EmployeePerformanceConfigurationFormType[],
  employees: EmployeeFormType[],
) {
  const _performances = performances.map((performance) => {
    const count = employees.filter(
      (employee) => employee.grade === performance.grade,
    ).length;
    const percentage = performance.percentage;
    const dividenPercentage = generateDividenPercentage(percentage, count);
    return {
      ...performance,
      count,
      dividenPercentage,
    };
  });

  return _performances as EmployeePerformanceConfigurationFormType[];
}

export function generateJobLevelByEmployees(
  performances: EmployeeJobLevelConfigurationFormType[],
  employees: EmployeeFormType[],
) {
  const _performances = performances.map((performance) => {
    const count = employees.filter(
      (employee) => employee.jobLevel === performance.jobLevel,
    ).length;
    const percentage = performance.percentage;
    const dividenPercentage = generateDividenPercentage(percentage, count);
    return {
      ...performance,
      count,
      dividenPercentage,
    };
  });

  return _performances as EmployeeJobLevelConfigurationFormType[];
}

export function recalculatePerformanceConfigures(
  performances: EmployeePerformanceConfigurationFormType[],
) {
  let total = 100;
  return performances.map((performance) => {
    if (performance.grade === EmployeeGradeEnum.F) {
      const dividenPercentage = generateDividenPercentage(
        total,
        performance.count,
      );
      return {
        ...performance,
        percentage: total,
        dividenPercentage,
      };
    } else {
      total = fixFloatingPoint(total - (performance.percentage || 0));
      return performance;
    }
  }) as EmployeePerformanceConfigurationFormType[];
}

export function recalculateJobLevelConfigures(
  jobLevels: EmployeeJobLevelConfigurationFormType[],
) {
  let total = 100;
  return jobLevels.map((performance) => {
    if (performance.jobLevel === EmployeeJobLevelEnum.Director) {
      const dividenPercentage = generateDividenPercentage(
        total,
        performance.count,
      );
      return {
        ...performance,
        percentage: total,
        dividenPercentage,
      };
    } else {
      total = fixFloatingPoint(total - (performance.percentage || 0));
      return performance;
    }
  }) as EmployeeJobLevelConfigurationFormType[];
}

export function defaultValuesEmployeeJobLevelConfigurations() {
  const jobLevels = [
    EmployeeJobLevelEnum.NonSales,
    EmployeeJobLevelEnum.Sales,
    EmployeeJobLevelEnum.Spv,
    EmployeeJobLevelEnum.Manager,
    EmployeeJobLevelEnum.Director,
  ];

  return jobLevels.map((jobLevel) => {
    return {
      id: undefined,
      jobLevel,
      percentage: jobLevel === EmployeeJobLevelEnum.Director ? 100 : undefined,
      count: 0,
      dividenPercentage: undefined,
      customId: crypto.randomUUID(),
    };
  }) as never as EmployeeJobLevelConfigurationFormType[];
}

export function defaultValuesEmployeePerformanceConfigurations() {
  const grades = [
    EmployeeGradeEnum.A,
    EmployeeGradeEnum.B,
    EmployeeGradeEnum.C,
    EmployeeGradeEnum.D,
    EmployeeGradeEnum.E,
    EmployeeGradeEnum.F,
  ];

  return grades.map((grade) => {
    return {
      id: undefined,
      grade,
      percentage: undefined,
      count: 0,
      dividenPercentage: undefined,
      customId: crypto.randomUUID(),
    };
  }) as never as EmployeePerformanceConfigurationFormType[];
}

export function defaultValuesSalesConfigurations() {
  const salesDepartment = [
    SaleDepartmentEnum.Activity,
    SaleDepartmentEnum.Cruise,
    SaleDepartmentEnum.Document,
    SaleDepartmentEnum.Flight,
    SaleDepartmentEnum.Hotel,
    SaleDepartmentEnum.Product,
  ];
  return salesDepartment.map((department) => {
    return {
      id: undefined,
      department,
      operatorSaleBonus: undefined,
      leadBonus: undefined,
      type: CalculationTypeEnum.Percentage,
      customId: crypto.randomUUID(),
    };
  }) as never as SaleConfigurationFormType[];
}

export default function useGetDefaultValues(
  bonusPeriod?: BonusPeriodModel,
  previousEmployees?: PreviousBonusPeriodEmployeesModel[],
): BonusPeriodFormType {
  const defaultValues = React.useMemo(() => {
    return {
      employees:
        bonusPeriod?.employees.map((employee) => {
          const previousData = previousEmployees?.find(
            (pe) => pe.employee.id === employee.employee.id,
          );
          return {
            customId: crypto.randomUUID(),
            employeeId: employee.employee.id,
            grade: employee.grade,
            jobLevel: employee.jobLevel,
            id: employee.id,
            previousGrade: previousData?.grade || '',
            previousJobLevel: previousData?.jobLevel || '',
            role: (employee.employee.roles || [])
              .map((role) => role.name)
              .join(', '),
          };
        }) ||
        previousEmployees?.map((employee) => {
          return {
            customId: crypto.randomUUID(),
            employeeId: employee.employee.id,
            grade: '',
            jobLevel: '',
            id: undefined,
            previousGrade: employee.grade,
            previousJobLevel: employee.jobLevel,
            role: (employee.employee.roles || [])
              .map((role) => role.name)
              .join(', '),
          };
        }) ||
        [],
      employeeJobLevelConfigurations:
        bonusPeriod?.employeeJobLevelConfigurations?.map((ejc) => {
          return {
            count: ejc.count,
            customId: crypto.randomUUID(),
            dividenPercentage: generateDividenPercentage(
              ejc.percentage,
              ejc.count,
            ) as number,
            id: ejc.id,
            jobLevel: ejc.jobLevel,
            percentage: ejc.percentage,
          };
        }) ?? defaultValuesEmployeeJobLevelConfigurations(),
      employeePerformanceConfigurations:
        bonusPeriod?.employeePerformanceConfigurations?.map((epc) => {
          return {
            count: epc.count,
            customId: crypto.randomUUID(),
            dividenPercentage: generateDividenPercentage(
              epc.percentage,
              epc.count,
            ) as number,
            grade: epc.grade,
            id: epc.id,
            percentage: epc.percentage,
          };
        }) ?? defaultValuesEmployeePerformanceConfigurations(),
      endAt: bonusPeriod?.endAt || null,
      issueAt: bonusPeriod?.issueAt || null,
      name: bonusPeriod?.name || '',
      profitPercentage:
        bonusPeriod?.profitPercentage ?? (undefined as never as number),
      saleConfigurations:
        bonusPeriod?.saleConfigurations?.map((sc) => {
          return {
            customId: crypto.randomUUID(),
            department: sc.department,
            id: sc.id,
            leadBonus: sc.leadBonus,
            operatorSalesBonus: sc.operatorSalesBonus,
            type: sc.type,
          };
        }) ?? defaultValuesSalesConfigurations(),
      startAt: bonusPeriod?.startAt || null,
      bonusPeriod,
    };
  }, [bonusPeriod, previousEmployees]);
  return defaultValues;
}
